import React, { useState, useEffect, useRef } from 'react';
import { Button } from 'aha-components';
import PlatformsTab from './PlatformsTab';
import { deliveryItems, trendDeliveryItems } from './config';
import { PlatformItems } from '@/interface/common';
import { isMobile } from '@/biz/tool';
import MobileLine from './MobileLine';
import MobileSteps from './MobileSteps';
import { useTranslation } from 'react-i18next';
import { useWebModalStore } from '@/hooks/useWebModalStore';

interface Props {
  step: number;
  setStep: Function;
  platform: string;
  contentType: string;
  maxStep: number;
  setMaxStep: Function;
  isAffiliate: boolean;
}

const Delivery: React.FC<Props> = ({
  step,
  setStep,
  platform,
  contentType,
  maxStep,
  setMaxStep,
  isAffiliate
}) => {
  const { t } = useTranslation();
  const { educationInfo } = useWebModalStore();
  const { campaign } = educationInfo || {};
  const [time, setTime] = useState(10);
  const timeRef = useRef(time);

  // 同步 time 到 ref
  useEffect(() => {
    timeRef.current = time;
  }, [time]);

  useEffect(() => {
    const educationModalEnd = localStorage.getItem('educationModalEnd');

    // 如果已经完成或已结束，直接设置为0
    if (maxStep > step || !!educationModalEnd) {
      setTime(0);
      return;
    }

    // 如果时间大于0，开始倒计时
    if (timeRef.current > 0) {
      const timer = setTimeout(() => {
        setTime((prevTime) => prevTime - 1);
      }, 1000);

      return () => clearTimeout(timer);
    }
  }, [time, maxStep, step]); // 添加所有依赖项

  useEffect(() => {
    localStorage.setItem('educationModalStart', '1');
  }, []);
  const [value, setValue] = useState('Dedicated video');
  const [currentPlatform, setCurrentPlatform] = useState(platform);
  const [key, setKey] = useState('');
  const [physicalType, setPhysicalType] = useState('No sample shipment');

  useEffect(() => {
    if (contentType) {
      setValue(contentType);
    }
  }, [contentType]);

  useEffect(() => {
    if (isAffiliate) {
      setKey(physicalType);
      return;
    }
    setKey(
      currentPlatform === PlatformItems.YOUTUBE
        ? value
          ? value
          : contentType
        : currentPlatform
    );
  }, [currentPlatform, value, physicalType]);

  return (
    <div
      className={`w-full bg-white pb-8 flex flex-col justify-between ${isMobile() ? 'px-0' : 'px-8'}`}
    >
      <div
        className={`w-full py-5 inline-flex flex-col justify-center items-start gap-2 ${isMobile() ? '' : ' border-b border-[#e8e8ec]'}`}
      >
        <div className="justify-start text-[#1c2024] text-2xl font-semibold font-['Plus_Jakarta_Sans'] leading-normal">
          {t('modal.education.delivery.title', {
            defaultValue: 'Delivery timeline'
          })}
        </div>
        {isMobile() && (
          <MobileSteps step={step} maxStep={maxStep} setStep={setStep} />
        )}

        <div className="w-full justify-center text-[#60646c] text-sm font-normal font-['Inter'] leading-tight">
          {t('modal.education.delivery.description', {
            defaultValue:
              'Understand the collaboration delivery timeline to ensure a successful collaboration on Aha Creator'
          })}
        </div>
      </div>
      {isMobile() && <MobileLine />}
      <div className={`${isMobile() ? '' : 'h-[540px] pb-8 overflow-y-auto'}`}>
        {!isAffiliate && (
          <PlatformsTab
            platform={currentPlatform}
            setPlatform={setCurrentPlatform}
          />
        )}
        {isAffiliate ? (
          <div className="bg-[#F9F9FB] rounded-[16px] p-4 mt-6">
            <div className="inline-flex justify-start items-center gap-[11px] mb-8">
              <div
                className={`text-center justify-center  text-sm font-semibold font-['Plus_Jakarta_Sans'] leading-tight cursor-pointer ${
                  physicalType === 'No sample shipment'
                    ? 'text-[#1c2024]'
                    : 'text-[#8b8d98]'
                }`}
                onClick={() => setPhysicalType('No sample shipment')}
              >
                {t('modal.education.delivery.no_sample_shipment', {
                  defaultValue: 'No sample shipment'
                })}
              </div>
              <div className="w-[1px] bg-[#d9d9e0] h-[15px]"></div>
              <div
                className={`text-center justify-center  text-sm font-semibold font-['Plus_Jakarta_Sans'] leading-tight cursor-pointer ${
                  physicalType === 'With sample shipment'
                    ? 'text-[#1c2024]'
                    : 'text-[#8b8d98]'
                }`}
                onClick={() => setPhysicalType('With sample shipment')}
              >
                {t('modal.education.delivery.with_sample_shipment', {
                  defaultValue: 'With sample shipment'
                })}
              </div>
            </div>
            <img
              src={
                physicalType === 'No sample shipment'
                  ? 'https://website-resource.ahaglobal.io/ads-platform/ads-guide-no-sample-shipment.png'
                  : 'https://website-resource.ahaglobal.io/ads-platform/ads-guide-sample-shipment.png'
              }
              alt=""
            />
          </div>
        ) : (
          <>
            {currentPlatform === PlatformItems.YOUTUBE ? (
              <div className="bg-[#F9F9FB] rounded-[16px] p-4 mt-6">
                <div className="inline-flex justify-start items-center gap-[11px] mb-8">
                  <div
                    className={`text-center justify-center  text-sm font-semibold font-['Plus_Jakarta_Sans'] leading-tight cursor-pointer ${
                      value === 'Dedicated video'
                        ? 'text-[#1c2024]'
                        : 'text-[#8b8d98]'
                    }`}
                    onClick={() => setValue('Dedicated video')}
                  >
                    Dedicated video
                  </div>
                  <div className="w-[1px] bg-[#d9d9e0] h-[15px]"></div>
                  <div
                    className={`text-center justify-center  text-sm font-semibold font-['Plus_Jakarta_Sans'] leading-tight cursor-pointer ${
                      value === 'Integrated video'
                        ? 'text-[#1c2024]'
                        : 'text-[#8b8d98]'
                    }`}
                    onClick={() => setValue('Integrated video')}
                  >
                    Integrated video
                  </div>
                </div>
                {campaign?.isTrendCampaign ? (
                  <img
                    src={
                      isMobile()
                        ? 'https://website-resource.ahaglobal.io/ads-platform/ads-guide-trend-vertical.png'
                        : 'https://website-resource.ahaglobal.io/ads-platform/ads-guide-trend.png'
                    }
                    alt=""
                  />
                ) : (
                  <img
                    src={
                      value === 'Dedicated video'
                        ? isMobile()
                          ? 'https://website-resource.ahaglobal.io/ads-platform/ads-guide-dedicated-order-vertical-new.png'
                          : 'https://website-resource.ahaglobal.io/ads-platform/ads-guide-dedicated-order-new.png'
                        : isMobile()
                          ? 'https://website-resource.ahaglobal.io/ads-platform/ads-guide-integrated-order-vertical-new.png'
                          : 'https://website-resource.ahaglobal.io/ads-platform/ads-guide-integrated-order-new.png'
                    }
                    alt=""
                  />
                )}
              </div>
            ) : (
              <div
                className={`w-full mt-6 ${isMobile() ? '' : 'bg-[#f9f9fb] p-4 rounded-xl'}`}
              >
                {campaign?.isTrendCampaign ? (
                  <img
                    src={
                      isMobile()
                        ? 'https://website-resource.ahaglobal.io/ads-platform/ads-guide-trend-vertical.png'
                        : 'https://website-resource.ahaglobal.io/ads-platform/ads-guide-trend.png'
                    }
                    className="w-full"
                    alt=""
                  />
                ) : (
                  <img
                    src={
                      isMobile()
                        ? deliveryItems?.[key]?.verticalImgUrl
                        : deliveryItems?.[key]?.img
                    }
                    className="w-full"
                    alt=""
                  />
                )}
              </div>
            )}
          </>
        )}

        <div className="self-stretch px-2 inline-flex flex-col justify-start items-start gap-3 mt-6">
          <div className="self-stretch justify-start">
            <span className="text-[#1c2024] text-base font-semibold font-['Inter'] leading-loose">
              {t('modal.education.delivery.notes_title', {
                defaultValue: 'Notes:'
              })}
              <br />
            </span>
            <ul className="list-disc pl-4">
              {campaign?.isTrendCampaign ? (
                <>
                  {trendDeliveryItems?.notes?.map((item: string) => (
                    <li key={item}>
                      <span
                        dangerouslySetInnerHTML={{ __html: item }}
                        className="text-[#1c2024] text-base font-normal font-['Inter'] leading-loose"
                      ></span>
                    </li>
                  ))}
                </>
              ) : (
                <>
                  {deliveryItems?.[key]?.notes?.map((item: string) => (
                    <li key={item}>
                      <span
                        dangerouslySetInnerHTML={{ __html: item }}
                        className="text-[#1c2024] text-base font-normal font-['Inter'] leading-loose"
                      ></span>
                    </li>
                  ))}
                </>
              )}
            </ul>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-end gap-4 mt-8">
        <Button
          type="primary"
          className="!w-[160px]"
          onClick={() => {
            setStep(step + 1);
            setMaxStep(Math.max(maxStep, step + 1));
          }}
        >
          {t('modal.education.delivery.next_btn', { defaultValue: 'Next' })}{' '}
        </Button>
      </div>
    </div>
  );
};

export default Delivery;
